import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, onSnapshot, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from './AuthContext';

// Création du contexte
const CompanyContext = createContext(null);

// Hook personnalisé pour utiliser le contexte
export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany doit être utilisé dans un CompanyProvider');
  }
  return context;
};

// Provider du contexte
export const CompanyProvider = ({ children }) => {
  const { user, userPlan } = useAuth();

  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [isRepairing, setIsRepairing] = useState(false);

  // Fonction pour s'abonner aux entreprises de l'utilisateur
  useEffect(() => {
    if (!user) {
      setUserCompanies([]);
      setActiveCompanyId(null);
      setLoadingCompanies(false);
      return;
    }

    setLoadingCompanies(true);
    const userRef = doc(db, 'users', user.uid);
    const companyUnsubscribers = [];

    const unsubscribeUser = onSnapshot(userRef, (userSnap) => {
      const userData = userSnap.exists() ? userSnap.data() : {};
      const companyIds = userData.companyIds || [];

      // Nettoyer les anciens listeners
      companyUnsubscribers.forEach(unsub => unsub());
      companyUnsubscribers.length = 0;

      if (companyIds.length > 0) {
        const companiesMap = new Map();
        let loadedCount = 0;

        companyIds.forEach(companyId => {
          try {
            const companyRef = doc(db, 'companies', companyId);
            const memberRef = doc(db, 'companies', companyId, 'members', user.uid);

            const unsubCompany = onSnapshot(companyRef, async (companySnap) => {
              if (companySnap.exists()) {
                const companyData = { id: companyId, ...companySnap.data() };

                try {
                  const memberSnap = await getDoc(memberRef);
                  if (memberSnap.exists()) {
                    companyData.role = memberSnap.data().role || memberSnap.data().roles || 'member';
                  } else if (companyData.ownerUid === user.uid) {
                    companyData.role = 'owner';
                  } else {
                    companyData.role = 'viewer';
                  }
                } catch (err) {
                  console.warn(`[CompanyContext] Error fetching member role for ${companyId}:`, err);
                  companyData.role = companyData.ownerUid === user.uid ? 'owner' : 'viewer';
                }

                companiesMap.set(companyId, companyData);
              }
              loadedCount++;

              if (loadedCount === companyIds.length) {
                const companies = Array.from(companiesMap.values());
                setUserCompanies(companies);
                setLoadingCompanies(false);
              }
            }, async (err) => {
              console.warn(`[CompanyContext] Error listening to company ${companyId}:`, err);

              if (err.code === 'permission-denied' || err.message.includes('Missing or insufficient permissions')) {
                try {
                  const updatedCompanyIds = companyIds.filter(id => id !== companyId);
                  await setDoc(doc(db, 'users', user.uid), { companyIds: updatedCompanyIds }, { merge: true });
                } catch (cleanupErr) {
                  console.warn(`[CompanyContext] Failed to cleanup company ${companyId}:`, cleanupErr);
                }
              }

              loadedCount++;
              if (loadedCount === companyIds.length) {
                const companies = Array.from(companiesMap.values());
                setUserCompanies(companies);
                setLoadingCompanies(false);
              }
            });
            companyUnsubscribers.push(unsubCompany);
          } catch (err) {
            console.warn(`[CompanyContext] Error setting up listener for company ${companyId}:`, err);
            loadedCount++;
            if (loadedCount === companyIds.length) {
              const companies = Array.from(companiesMap.values());
              setUserCompanies(companies);
              setLoadingCompanies(false);
            }
          }
        });
      } else {
        setUserCompanies([]);
        setLoadingCompanies(false);
      }
    }, (err) => {
      console.warn('[CompanyContext] Error loading user doc', err);
      setLoadingCompanies(false);
    });

    return () => {
      unsubscribeUser();
      companyUnsubscribers.forEach(unsub => unsub());
    };
  }, [user]);

  // Auto-sélection de l'entreprise active
  useEffect(() => {
    if (loadingCompanies) return;
    if (userCompanies.length > 0 && !activeCompanyId) {
      setActiveCompanyId(userCompanies[0].id);
    }
    if (activeCompanyId && userCompanies.length > 0 && !userCompanies.find(c => c.id === activeCompanyId)) {
      setActiveCompanyId(userCompanies[0].id);
    }
  }, [loadingCompanies, userCompanies, activeCompanyId]);

  // Fonction de réparation du profil
  const handleRepairProfile = async () => {
    if (!user) return;
    setIsRepairing(true);

    try {
      const recoveryQuery = query(collectionGroup(db, 'members'), where('uid', '==', user.uid));
      const recoverySnap = await getDocs(recoveryQuery);

      if (!recoverySnap.empty) {
        const foundCompanyIds = recoverySnap.docs.map(d => d.ref.parent.parent.id).filter(Boolean);
        const uniqueIds = [...new Set(foundCompanyIds)];

        if (uniqueIds.length > 0) {
          await setDoc(doc(db, 'users', user.uid), { companyIds: uniqueIds }, { merge: true });
          alert("Profil resynchronisé ! Vos entreprises devraient apparaître.");
        } else {
          alert("Aucune entreprise trouvée. Veuillez en créer une.");
        }
      } else {
        alert("Aucune association trouvée dans la base de données. Vous devez créer une entreprise.");
      }
    } catch (err) {
      console.error("ERREUR DE RÉPARATION :", err);
      if (err.message && err.message.includes('index')) {
        alert("Une configuration (Index) manque dans Firebase. Ouvrez la console développeur (F12) et cliquez sur le lien fourni par Firebase.");
      } else {
        alert("Erreur technique. Vérifiez la console pour plus de détails.");
      }
    }
    setIsRepairing(false);
  };

  // Valeurs dérivées mémorisées
  const activeCompany = useMemo(() =>
    userCompanies.find(c => c.id === activeCompanyId),
    [userCompanies, activeCompanyId]
  );

  const companyPlan = useMemo(() =>
    activeCompany?.plan || 'free',
    [activeCompany]
  );

  const userRole = useMemo(() =>
    activeCompany?.role || 'viewer',
    [activeCompany]
  );

  const canAccessSites = useMemo(() =>
    (companyPlan && companyPlan.includes('business')) || userPlan === 'beta_tester',
    [companyPlan, userPlan]
  );

  const canAccessFinancial = useMemo(() =>
    ['owner', 'admin', 'manager', 'editor'].includes(userRole),
    [userRole]
  );

  const canManageCompanies = useMemo(() =>
    (companyPlan && companyPlan.includes('business')) || userPlan === 'beta_tester',
    [companyPlan, userPlan]
  );

  const hasCompany = useMemo(() =>
    userCompanies.length > 0,
    [userCompanies]
  );

  const value = {
    // État
    activeCompanyId,
    setActiveCompanyId,
    userCompanies,
    loadingCompanies,
    isRepairing,

    // Valeurs dérivées
    activeCompany,
    companyPlan,
    userRole,
    canAccessSites,
    canAccessFinancial,
    canManageCompanies,
    hasCompany,

    // Actions
    handleRepairProfile
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
